import { useUIStore, useMovieStore, useEditorStore } from '../../../stores';
import { usePatch } from '../../../hooks';
import { useTranslation } from '../../../i18n';
import type { MovieResponse } from '../../../types';
import { Toolpad } from '../Toolpad';
import { PropertyInput } from '../../ui/PropertyInput';
import { useRef, useEffect, useState } from 'react';
import { Select } from '@radix-ui/themes';
import { publishMovie, resolvePlayerUrl } from '../../../util/publishMovie';

export function MoviePropertiesToolpad() {
  const { t } = useTranslation();
  const { toolpads, toggleToolpad } = useUIStore();
  const { currentMovie, updateMovie } = useMovieStore();
  const { setFps } = useEditorStore();
  const isOpen = toolpads.movieProperties.isOpen;
  const movieId = currentMovie?.id ? Number(currentMovie.id) : null;
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishLink, setPublishLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const updateMovieUrl =
    movieId && !Number.isNaN(movieId) ? `/api/movies/${movieId}/update` : null;
  const { execute: saveMovieToApi, error: saveMovieApiError } = usePatch<
    MovieResponse,
    {
      name: string;
      description: string;
      width: number;
      height: number;
      fps: number;
    }
  >(updateMovieUrl);
  const hasPendingSaveRef = useRef(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!currentMovie || !movieId || Number.isNaN(movieId)) return;
    if (!hasPendingSaveRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      hasPendingSaveRef.current = false;
      void saveMovieToApi({
        name: currentMovie.name,
        description: currentMovie.description,
        width: currentMovie.width,
        height: currentMovie.height,
        fps: currentMovie.fps,
      });
    }, 600);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    currentMovie,
    movieId,
    saveMovieToApi,
    currentMovie?.name,
    currentMovie?.description,
    currentMovie?.width,
    currentMovie?.height,
    currentMovie?.fps,
  ]);

  const updateMovieWithDelaySave = (updates: {
    width?: number;
    height?: number;
    fps?: number;
  }) => {
    hasPendingSaveRef.current = true;
    updateMovie(updates);
    if (typeof updates.fps === 'number') {
      setFps(updates.fps);
    }
  };

  const handleFpsChange = (fps: number) => {
    updateMovieWithDelaySave({ fps });
  };

  const handlePublish = async () => {
    if (!movieId || Number.isNaN(movieId) || publishing) return;
    setPublishing(true);
    setPublishError(null);
    setCopied(false);
    try {
      const result = await publishMovie(movieId);
      const url = resolvePlayerUrl(result);
      setPublishLink(url);
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      } catch {
        // clipboard may be blocked; link still shown
      }
    } catch (e: unknown) {
      setPublishError(e instanceof Error ? e.message : String(e));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Toolpad
      title={t('toolpads.movieProperties.title')}
      isOpen={isOpen}
      onToggle={() => toggleToolpad('movieProperties')}
    >
      {currentMovie ? (
        <div className="movie-properties">
          {saveMovieApiError && <div className="error-message">{saveMovieApiError}</div>}
          <div className="property-section">
            <div className="property-section-title">
              {t('toolpads.movieProperties.stageSize')}
            </div>
            <div className="property-grid">
              <PropertyInput
                label={t('toolpads.movieProperties.width')}
                value={currentMovie.width}
                onChange={(v) => updateMovieWithDelaySave({ width: v })}
                min={100}
                max={4096}
              />
              <PropertyInput
                label={t('toolpads.movieProperties.height')}
                value={currentMovie.height}
                onChange={(v) => updateMovieWithDelaySave({ height: v })}
                min={100}
                max={4096}
              />
            </div>
            <div className="preset-sizes">
              <button
                className="btn btn-tiny"
                onClick={() => updateMovieWithDelaySave({ width: 800, height: 600 })}
              >
                800×600
              </button>
              <button
                className="btn btn-tiny"
                onClick={() => updateMovieWithDelaySave({ width: 1280, height: 720 })}
              >
                720p
              </button>
              <button
                className="btn btn-tiny"
                onClick={() => updateMovieWithDelaySave({ width: 1920, height: 1080 })}
              >
                1080p
              </button>
            </div>
          </div>

          <div className="property-section">
            <div className="property-section-title">
              {t('toolpads.movieProperties.frameRate')}
            </div>
            <div className="property-row-full">
              <Select.Root
                value={String(currentMovie.fps)}
                onValueChange={(value) => handleFpsChange(Number.parseInt(value, 10))}
                size="1"
              >
                <Select.Trigger
                  variant="surface"
                  className="style-select-trigger"
                  aria-label={t('toolpads.movieProperties.frameRateAria')}
                />
                <Select.Content position="popper" sideOffset={4}>
                  {[12, 24, 30, 60].map((fps) => (
                    <Select.Item key={fps} value={String(fps)}>
                      {t('toolpads.movieProperties.fpsOption', { count: fps })}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </div>
          </div>

          <div className="property-section">
            <div className="property-section-title">{t('toolpads.movieProperties.info')}</div>
            <div className="movie-info">
              <div>
                {t('toolpads.movieProperties.name')}
                <strong>{currentMovie.name}</strong>
              </div>
              <div>
                {t('toolpads.movieProperties.scenes')}
                <strong>{currentMovie.scenes.length}</strong>
              </div>
            </div>
          </div>

          <div className="property-section">
            <div className="property-section-title">
              {t('toolpads.movieProperties.publish')}
            </div>
            <p className="movie-properties__publish-hint">
              {t('toolpads.movieProperties.publishHint')}
            </p>
            <button
              type="button"
              className="btn btn-tiny"
              onClick={() => void handlePublish()}
              disabled={publishing || !movieId}
            >
              {publishing
                ? t('toolpads.movieProperties.publishing')
                : t('toolpads.movieProperties.publish')}
            </button>
            {publishError && <div className="error-message">{publishError}</div>}
            {publishLink && (
              <div className="movie-properties__publish-result">
                <a href={publishLink} target="_blank" rel="noreferrer">
                  {publishLink}
                </a>
                {copied && (
                  <span className="movie-properties__copied">
                    {t('toolpads.movieProperties.linkCopied')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="no-selection-message">{t('toolpads.movieProperties.noMovie')}</div>
      )}
    </Toolpad>
  );
}
